import { Fragment, memo, useEffect, useMemo, useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import Breadcrumbs, { BreadcrumbItem } from 'shell/components/shared/breadcrumb';
import { CommonCivilStatusCrumbs } from 'civil-status/constants/breadcrumb';
import StepForm from 'civil-status/components/step/form';
import { Grid, useEventCallback } from '@mui/material';
import { Form, Formik } from 'formik';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { HttpResponse } from 'shell/models/http-response';
import {
  ViewStepVM,
  ParentVM,
  ResponsePreview,
  ErrorsWarnings,
  CommunicationVM,
} from 'civil-status/view-models/step-form';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { SubjectsWizardUseCase } from 'shell/models/subjects-wizard';
import { ActCategory, ModelEventoDTO } from 'shell/models/act-category-list';
import { transformerActAnsc } from 'civil-status/utils/transformer-act-ansc';
import { noop } from 'shell/utils/noop';
import { CodeSubject } from 'civil-status/constants/code-subject-section';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { toast } from 'react-toastify';
import { AxiosResponse } from 'axios';
import { ItemVM } from 'shell/view-models/item';
import { ErrorWarningToVisualizeType } from '../step';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { ModalViewerPDF } from 'shell/components/shared/pdf-modal';
import ModalPrintSuccess from 'practice/components/detail/form/print-comunication/modal';
import { CommunicationRes } from 'shell/models/communications';
import { blobToBase64 } from 'shell/utils/base64-to-blob';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { ResearchNotifications } from 'notifications-from-civil-status/view-models/notifications-list';
import { titleMotherFather } from 'civil-status/constants/title-mother-father';
import { IdStateNotification } from 'notifications-from-civil-status/constants/id-state-notification';
import { isResidenceMilano } from 'civil-status/utils/residence-milano';
import DeleteDialog from 'shell/components/shared/delete-dialog';
import TitleStep from 'civil-status/components/shared/title-step';
import { MUNICIPALITY_MILANO } from 'civil-status/constants/municipality-milano';

const isDetail = true; // dati provenineti da ANSC quindi non modificabili e sempre disabilitato
const isView = true; // fixed

interface StepPageProps {
  isResearchPerson?: boolean;
  isNotifications?: boolean;
  isNotificationsAnsc?: boolean;
  isCommunications?: boolean;
  isWithoutBreadcrumbs?: boolean;
  isDetailAcquisitionNotifications?: boolean;
  isEditAcquisitionNotifications?: boolean;
  baseUrl?: string;
  items?: BreadcrumbItem[];
}

const StepPageComponent = ({
  isResearchPerson = false,
  isNotifications = false,
  isNotificationsAnsc = false,
  isCommunications = false,
  isWithoutBreadcrumbs = false,
  baseUrl,
  isDetailAcquisitionNotifications = false,
  isEditAcquisitionNotifications = false,
  items,
}: StepPageProps): React.ReactElement => {
  // stato per errori e warning del bottone ANTEPRIMA
  const [errorWarningToVisualize, setErrorWarningToVisualize] =
    useState<ErrorWarningToVisualizeType>({
      visibilityError: false,
      titleError: '',
      messagesError: [],
      visibilityWarning: false,
      titleWarning: '',
      messagesWarning: [],
    });
  // stato per stampa anteprima
  const [pdfModal, setPdfModal] = useState({
    data: '',
    open: false,
    name: '',
    isCommunication: false,
  });
  // ModalPrintSuccess
  const [modalPrintSuccess, setModalPrintSuccess] = useState<{
    open: boolean;
    total?: number;
    isCommunication?: boolean;
    valuesCommunication?: CommunicationVM;
  }>({
    open: false,
  });
  const [dataPrint, setDataPrint] = useState<{
    modelEvento?: ModelEventoDTO;
    codiceStampa?: string;
    cdCondizione?: string;
  }>({});
  const [modalNotificationToDelete, setModalNotificationToDelete] = useState<boolean>(false);

  const navigate = useNavigate();

  const params = useParams();

  const idAnsc = params.id || undefined;
  const idCat = params.idCat || '';
  const valueCat = params.valueCat || '';
  const idNotification = params.idNotification || '';
  const stateNotification = params.stateNotification || '';
  const idNotifica = params.idNotifica || '';

  const researchCatPath = `/stato-civile/${valueCat}/${idCat}/ricerca`;
  const researchPath = `/stato-civile/ricerca-atti`;
  const researchNotificationsPath =
    idCat || valueCat
      ? `/stato-civile/notifiche/ansc/ricerca/${valueCat}/${idCat}`
      : `/stato-civile/notifiche/ansc/ricerca`;
  const researchCommunications = `/stato-civile/ricerca-comunicazioni/${valueCat}/${idCat}`;

  const [controlsCommunications, getControlsCommunications] = useHttpFetch<
    HttpResponse<CommunicationRes>,
    CommunicationRes
  >(`${__ACT_API_URL__}/comunicazioni/controlli`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: {},
    },
    transformer: (res) => res.data.payload,
  });

  const [act, getAct] = useHttpFetch<HttpResponse<ActCategory>, ViewStepVM>('', {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: {},
    },
    transformer: (res) => {
      const modelEventoResList = res.data.payload.consultazioneANSCResponse?.modelEventoDTO || [];

      if (isCommunications && modelEventoResList?.length) {
        (
          getControlsCommunications({
            data: modelEventoResList[0],
          }) as ResponsePromise<HttpResponse<Array<CommunicationRes>>, Array<CommunicationRes>>
        ).then((resControlsCommunications) => {
          const castedResponse = (resControlsCommunications || []) as Array<CommunicationRes>;

          setDataPrint({
            modelEvento: modelEventoResList[0] || {},
            codiceStampa:
              castedResponse[0]?.templateComunicazioneVO?.codiceTemplateStampa || undefined,
            cdCondizione:
              castedResponse[0]?.comunicazioniUsecaseVO?.key?.codCondizione ||
              castedResponse[0]?.comunicazioniUsecaseVO?.codCondizione ||
              undefined,
          });
        });
      }
      return transformerActAnsc(res);
    },
  });

  const generateOathTranscript = useHttpClient('', {
    options: {
      method: 'GET',
      loading: true,
      errorCatching: true,
    },
  });

  const transcribeOath = useEventCallback((currentIdEvento?: number) => {
    if (!currentIdEvento) {
      return;
    }

    (
      generateOathTranscript({
        url: `${__ACT_API_URL__}/bozza/genera/trascrizione-giuramento/${currentIdEvento}`,
      }) as unknown as ResponsePromise<HttpResponse<{ idEvento?: number }>>
    ).then((response) => {
      const oathIdEvento = response?.data.payload.idEvento;

      if (oathIdEvento) {
        navigate({
          pathname: generatePath('/stato-civile/ricerca-atti/visualizza/:id/:idEvento', {
            id: idAnsc || '',
            idEvento: oathIdEvento?.toString(),
          }),
        });
        toast.success('Operazione eseguita con successo');
      } else {
        toast.error("Errore nella generazione dell'atto");
      }
    });
  });

  useEffect(() => {
    const dataEvento = {
      criteriRicercaEvento: {
        idAnsc,
        flagRettifica: true,
      },
    };

    const dataSoggetto = {
      criteriRicercaSoggetto: {
        idAnscSoggetto: idAnsc,
      },
    };

    getAct({
      url: isResearchPerson
        ? `${__ACT_API_URL__}/consultazione/soggetto`
        : `${__ACT_API_URL__}/consultazione/evento/aggiornato`,
      data: isResearchPerson ? dataSoggetto : dataEvento,
    });
  }, [idAnsc, isResearchPerson]);

  const soggettiUseCaseData: Array<SubjectsWizardUseCase> = useMemo(() => {
    const castedData = (act?.data || {}) as Record<string, ParentVM>;
    const arrKeys = Object.keys(castedData);

    const res: Array<SubjectsWizardUseCase> = [];

    arrKeys.forEach((itemKey) => {
      if (itemKey === 'document') {
        // documenti gestiti diversamente
        return;
      }

      if (itemKey.includes(CodeSubject.MADRE)) {
        res.push({
          nomeSoggetto: titleMotherFather.Madre,
          codSoggetto: castedData[itemKey]?.codeSubject || '',
          configurazione: castedData[itemKey]?.configuration || '',
          flagComparente: castedData[itemKey]?.flagComparente || '',
          flagDichiarante: castedData[itemKey]?.flagDichiarante || '',
          tipoSoggetto: castedData[itemKey]?.tipoSoggetto || '',
          flagFirmatario: castedData[itemKey]?.flagFirmatario || '',
          presenza: castedData[itemKey]?.presenza || '',
        });

        return;
      }

      if (itemKey.includes(CodeSubject.PADRE)) {
        res.push({
          nomeSoggetto: titleMotherFather.Padre,
          codSoggetto: castedData[itemKey]?.codeSubject || '',
          configurazione: castedData[itemKey]?.configuration || '',
          flagComparente: castedData[itemKey]?.flagComparente || '',
          flagDichiarante: castedData[itemKey]?.flagDichiarante || '',
          tipoSoggetto: castedData[itemKey]?.tipoSoggetto || '',
          flagFirmatario: castedData[itemKey]?.flagFirmatario || '',
          presenza: castedData[itemKey]?.presenza || '',
        });

        return;
      }

      if (
        itemKey.includes(CodeSubject.INT_NAS) ||
        itemKey.includes(CodeSubject.INT_NAS2) ||
        itemKey.includes(CodeSubject.INT_NAS2_C1)
      ) {
        res.push({
          nomeSoggetto: 'Nascituro',
          codSoggetto: castedData[itemKey]?.codeSubject || '',
          configurazione: castedData[itemKey]?.configuration || '',
          flagComparente: castedData[itemKey]?.flagComparente || '',
          flagDichiarante: castedData[itemKey]?.flagDichiarante || '',
          tipoSoggetto: castedData[itemKey]?.tipoSoggetto || '',
          flagFirmatario: castedData[itemKey]?.flagFirmatario || '',
          presenza: castedData[itemKey]?.presenza || '',
        });

        return;
      }

      if (itemKey.includes(CodeSubject.INTERPR)) {
        res.push({
          nomeSoggetto: 'Interprete',
          codSoggetto: castedData[itemKey]?.codeSubject || '',
          configurazione: castedData[itemKey]?.configuration || '',
          flagComparente: castedData[itemKey]?.flagComparente || '',
          flagDichiarante: castedData[itemKey]?.flagDichiarante || '',
          tipoSoggetto: castedData[itemKey]?.tipoSoggetto || '',
          flagFirmatario: castedData[itemKey]?.flagFirmatario || '',
          presenza: castedData[itemKey]?.presenza || '',
        });

        return;
      }
    });

    return res;
  }, [act?.data]);

  const initialValues: ViewStepVM = useMemo(() => {
    return (act?.data || {}) as ViewStepVM;
  }, [act?.data]);

  const goBack = useEventCallback(() => {
    // tornare sulla navigazione
    navigate(
      baseUrl
        ? baseUrl
        : isCommunications
        ? researchCommunications
        : isNotifications
        ? researchNotificationsPath
        : idCat
        ? researchCatPath
        : researchPath
    );
  });

  const onNotification = useHttpClient('', {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const onClickNotification = useEventCallback((isConfirm?: boolean) => {
    if (!idNotification) {
      return;
    }

    const data = {
      idNotifica: idNotification,
    };

    (
      onNotification({
        data,
        url: isConfirm
          ? `${__ACT_API_URL__}/notifiche/conferma`
          : `${__ACT_API_URL__}/notifiche/rifiuta`,
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');
      goBack();
    });
  });

  const onPrintCommunication = useHttpClient(`${__ACT_API_URL__}/comunicazioni/stampa`, {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const onClickPrintCommunication = useEventCallback((values?: CommunicationVM) => {
    const data = {
      ...(dataPrint || {}),
      codiceStampa: values?.elementSelected || dataPrint.codiceStampa,
    };

    setModalPrintSuccess({ open: false });

    (
      onPrintCommunication({ data, responseType: 'blob' }) as unknown as ResponsePromise<unknown>
    ).then((response) => {
      if (response?.data) {
        const blob = new Blob([(response.data as string) || ''], { type: 'application/pdf' });

        blobToBase64(blob).then((base64) => {
          setModalPrintSuccess((oldState) => ({
            ...oldState,
            open: false,
            valuesCommunication: values,
          }));

          setPdfModal({
            data: base64.split(',')[1] as string,
            name: 'Stampa comunicazione',
            open: true,
            isCommunication: true,
          });
        });
      }
    });
  });

  const filterTable = useFilterTable<ResearchNotifications>();

  const saveDataNotification = useHttpClient(`${__ACT_API_URL__}/aggiornamento-anagrafe/save`, {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const onParent = useEventCallback((values: ViewStepVM, title?: string) => {
    const isWithoutTitle = !Boolean(title);
    const titleCheck = title || titleMotherFather.Madre;
    const valueParent = values.holders?.find((item) => item.title === titleCheck);

    if (!valueParent || !idNotifica) {
      return;
    }

    if (!isResidenceMilano(valueParent) && !isWithoutTitle) {
      // scelto un solo genitore
      toast.error(`Non si può procedere, soggetto non residente a Milano`);
      return;
    }

    if (isWithoutTitle) {
      // scelta opzione entrambi genitori
      const valueParentMadre = values.holders?.find(
        (item) => item.title === titleMotherFather.Madre
      );
      const valueParentPadre = values.holders?.find(
        (item) => item.title === titleMotherFather.Padre
      );

      if (!isResidenceMilano(valueParentMadre)) {
        toast.error(`Non si può procedere, Madre non residente a Milano`);
        return;
      }

      if (!isResidenceMilano(valueParentPadre)) {
        toast.error(`Non si può procedere, Padre non residente a Milano`);
        return;
      }
    }

    (
      saveDataNotification({
        data: {
          key: {
            idAggiornamentoAnagrafe: idNotifica,
          },
          cdStatoAggiornamento: IdStateNotification.InElaborazione,
        },
      }) as ResponsePromise<void>
    ).then(() => {
      filterTable.setValues({
        ...(filterTable.values || {}),
        searchPersonName: valueParent.name || '',
        searchPersonSurname: valueParent.surname || '',
        searchPersonDateBorn: valueParent.dateBorn || null,
        searchPersonIDAnpr: valueParent.idAnpr || '',
      });

      let redirectUrl = `${baseUrl}${
        isEditAcquisitionNotifications ? '/modifica' : '/visualizza'
      }/${idAnsc}/${idNotifica}/famiglia`;

      if (title === titleMotherFather.Madre) {
        redirectUrl = `${redirectUrl}/madre`;
      }

      if (title === titleMotherFather.Padre) {
        redirectUrl = `${redirectUrl}/padre`;
      }

      navigate(redirectUrl);
    });
  });

  const onRegisterDeath = useEventCallback((values: ViewStepVM, isNonResidente?: boolean) => {
    const valueHolder = values.holders ? values.holders[0] : undefined;

    if (!valueHolder || !idNotifica) {
      return;
    }

    // if (!isResidenceMilano(valueHolder)) {
    //   toast.error(`Non si può procedere, soggetto non residente a Milano`);
    //   return;
    // }

    (
      saveDataNotification({
        data: {
          key: {
            idAggiornamentoAnagrafe: idNotifica,
          },
          cdStatoAggiornamento: IdStateNotification.InElaborazione,
        },
      }) as ResponsePromise<void>
    ).then(() => {
      filterTable.setValues({
        ...(filterTable.values || {}),
        searchPersonName: valueHolder.name || '',
        searchPersonSurname: valueHolder.surname || '',
        searchPersonDateBorn: valueHolder.dateBorn || null,
        searchPersonIDAnpr: valueHolder.idAnpr || '',
      });

      let redirectUrl = `${baseUrl}${
        isEditAcquisitionNotifications ? '/modifica' : '/visualizza'
      }/${idAnsc}/${idNotifica}/morte`;

      const isHolderResidenceMilano =
        isResidenceMilano(valueHolder) ||
        valueHolder.municipalityAire?.id === MUNICIPALITY_MILANO.id ||
        valueHolder.municipalityAire?.description === MUNICIPALITY_MILANO.description;

      if (isHolderResidenceMilano && !isNonResidente) {
        redirectUrl += '/residenti';
      } else {
        redirectUrl += '/non-residenti';
      }

      navigate(redirectUrl);
    });
  });

  const onRegisterCitizenship = useEventCallback((values: ViewStepVM) => {
    const valueHolder = values.holders ? values.holders[0] : undefined;

    if (!valueHolder || !idNotifica) {
      return;
    }

    (
      saveDataNotification({
        data: {
          key: {
            idAggiornamentoAnagrafe: idNotifica,
          },
          cdStatoAggiornamento: IdStateNotification.InElaborazione,
        },
      }) as ResponsePromise<void>
    ).then(() => {
      filterTable.setValues({
        ...(filterTable.values || {}),
        searchPersonName: valueHolder.name || '',
        searchPersonSurname: valueHolder.surname || '',
        searchPersonDateBorn: valueHolder.dateBorn || null,
        searchPersonIDAnpr: valueHolder.idAnpr || '',
      });

      const redirectUrl = `${baseUrl}${
        isEditAcquisitionNotifications ? '/modifica' : '/visualizza'
      }/${idAnsc}/${idNotifica}/cittadinanza/ricerca`;

      navigate(redirectUrl);
    });
  });

  const onRegisterMarriageCivilUnion = useEventCallback(
    (values: ViewStepVM, isCivilUnion?: boolean, isNonResidente?: boolean) => {
      const valueHolders = values.holders || [];

      if (valueHolders.length === 0 || !idNotifica) {
        return;
      }

      const marito = valueHolders[0] || {};
      const moglie = valueHolders[1] || {};

      const isMaritoResidenceMilano =
        isResidenceMilano(marito) ||
        marito.municipalityAire?.id === MUNICIPALITY_MILANO.id ||
        marito.municipalityAire?.description === MUNICIPALITY_MILANO.description;

      const isMoglieResidenceMilano =
        isResidenceMilano(moglie) ||
        moglie.municipalityAire?.id === MUNICIPALITY_MILANO.id ||
        moglie.municipalityAire?.description === MUNICIPALITY_MILANO.description;

      (
        saveDataNotification({
          data: {
            key: {
              idAggiornamentoAnagrafe: idNotifica,
            },
            cdStatoAggiornamento: IdStateNotification.InElaborazione,
          },
        }) as ResponsePromise<void>
      ).then(() => {
        let valueHolder = undefined;

        if (
          (isMaritoResidenceMilano && isMoglieResidenceMilano) ||
          isMaritoResidenceMilano ||
          (!isMaritoResidenceMilano && !isMoglieResidenceMilano)
        ) {
          valueHolder = marito;
        } else {
          valueHolder = moglie;
        }

        filterTable.setValues({
          ...(filterTable.values || {}),
          searchPersonFiscalCode: valueHolder.fiscalCode || '',
          searchPersonName: valueHolder.name || '',
          searchPersonSurname: valueHolder.surname || '',
          searchPersonDateBorn: valueHolder.dateBorn || null,
          searchPersonIDAnpr: valueHolder.idAnpr || '',
          isMaritoResidenceMilano,
          isMoglieResidenceMilano,
        });

        let redirectUrl = `${baseUrl}${
          isEditAcquisitionNotifications ? '/modifica' : '/visualizza'
        }/${idAnsc}/${idNotifica}/`;

        if (isCivilUnion) {
          // Se è unione civile di non residenti a Milano e non siamo in unione civile
          if (!isNonResidente) {
            redirectUrl += 'unione-civile/residenti';
          } else {
            redirectUrl += 'unione-civile/non-residenti';
          }
        } else {
          // Se è matrimonio di non residenti a Milano e non siamo in unione civile
          if (!isNonResidente) {
            redirectUrl += 'matrimonio/residenti';
          } else {
            redirectUrl += 'matrimonio/non-residenti';
          }
        }

        navigate(redirectUrl);
      });
    }
  );

  const onRegisterBirth = useEventCallback((values: ViewStepVM) => {
    const valueHolder = values.holders ? values.holders[0] : undefined;

    if (!valueHolder || !idNotifica) {
      return;
    }

    (
      saveDataNotification({
        data: {
          key: {
            idAggiornamentoAnagrafe: idNotifica,
          },
          cdStatoAggiornamento: IdStateNotification.InElaborazione,
        },
      }) as ResponsePromise<void>
    ).then(() => {
      // Trovo madre e padre se ci sono e li setto nel context
      const father = values.holders?.find((el) => el.title === titleMotherFather.Padre);
      const mother = values.holders?.find((el) => el.title === titleMotherFather.Madre);

      filterTable.setValues({
        ...(filterTable.values || {}),
        searchPersonName: valueHolder.name || '',
        searchPersonSurname: valueHolder.surname || '',
        searchPersonDateBorn: valueHolder.dateBorn || null,
        searchPersonIDAnpr: valueHolder.idAnpr || '',
        searchPersonFiscalCode: valueHolder.fiscalCode || '',
        father,
        mother,
      });

      // Redirect verso pagina iscrizione non residenti
      const redirectUrl = `${baseUrl}${
        isEditAcquisitionNotifications ? '/modifica' : '/visualizza'
      }/${idAnsc}/${idNotifica}/nascita/non-residenti`;

      navigate(redirectUrl);
    });
  });

  const onOther = useEventCallback(() => {
    if (!idNotifica) {
      return;
    }

    (
      saveDataNotification({
        data: {
          key: {
            idAggiornamentoAnagrafe: idNotifica,
          },
          cdStatoAggiornamento: IdStateNotification.InElaborazione,
        },
      }) as ResponsePromise<void>
    ).then(() => {
      filterTable.setValues({
        ...(filterTable.values || {}),
        searchPersonName: '',
        searchPersonSurname: '',
        searchPersonDateBorn: null,
        searchPersonIDAnpr: '',
      });

      const redirectUrl = `${baseUrl}${
        isEditAcquisitionNotifications ? '/modifica' : '/visualizza'
      }/${idAnsc}/${idNotifica}/famiglia/altra`;

      navigate(redirectUrl);
    });
  });

  const onCohabitation = useEventCallback(() => {
    if (!idNotifica) {
      return;
    }

    (
      saveDataNotification({
        data: {
          key: {
            idAggiornamentoAnagrafe: idNotifica,
          },
          cdStatoAggiornamento: IdStateNotification.InElaborazione,
        },
      }) as ResponsePromise<void>
    ).then(() => {
      filterTable.setValues({
        ...(filterTable.values || {}),
        searchPersonName: '',
        searchPersonSurname: '',
        searchPersonDateBorn: null,
        searchPersonIDAnpr: '',
      });

      const redirectUrl = `${baseUrl}${
        isEditAcquisitionNotifications ? '/modifica' : '/visualizza'
      }/${idAnsc}/${idNotifica}/convivenza`;

      navigate(redirectUrl);
    });
  });

  const goBackNotification = useEventCallback(() => {
    goBack();
  });

  const onCancelNotification = useEventCallback(() => {
    setModalNotificationToDelete(true);
  });

  const clearToDeleteNotification = useEventCallback(() => {
    setModalNotificationToDelete(false);
  });

  const onTrashPressNotification = useEventCallback(() => {
    if (!idNotifica) {
      return;
    }

    (
      saveDataNotification({
        data: {
          key: {
            idAggiornamentoAnagrafe: idNotifica,
          },
          cdStatoAggiornamento: IdStateNotification.Annullata,
        },
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');
      goBack();
    });
  });

  const isLoading = act.isLoading || act.pristine || act.hasErrors;

  const onPreview = useHttpClient('', {
    options: {
      method: 'GET',
      loading: true,
      errorCatching: true,
    },
  });

  const [idAnscAct, setIdAnscAct] = useState<string>('');

  const onYesPreview = useEventCallback((idAnscActPreview?: string) => {
    if (idAnscActPreview) {
      setIdAnscAct((oldState) => (oldState === idAnscActPreview ? oldState : idAnscActPreview));
    }

    setModalPrintSuccess({ open: false });

    (
      onPreview({
        url: `${__ACT_API_URL__}/evento/anteprima/idAnsc/${idAnscActPreview || idAnsc}`,
      }) as unknown as ResponsePromise<HttpResponse<ResponsePreview>, HttpResponse<ResponsePreview>>
    ).then((response) => {
      toast.success('Operazione eseguita con successo');

      const responseCasted = response as AxiosResponse<HttpResponse<ResponsePreview>>;

      const castedErrors: Array<ErrorsWarnings> = responseCasted?.data.payload.errors || [];
      const titleError =
        castedErrors?.length > 1 ? `Ci sono ${castedErrors?.length} errori` : `C'è 1 errore`;
      const messagesError =
        (castedErrors?.map((item) => ({
          id: item.code,
          value: item.text,
        })) as Array<ItemVM>) || [];

      const castedWarnings: Array<ErrorsWarnings> = responseCasted?.data.payload.warnings || [];
      const titleWarning =
        castedWarnings?.length > 1 ? `Ci sono ${castedWarnings?.length} warning` : `C'è 1 warning`;
      const messagesWarning =
        (castedWarnings?.map((item) => ({
          id: item.code,
          value: item.text,
        })) as Array<ItemVM>) || [];

      setErrorWarningToVisualize({
        visibilityError: castedErrors?.length > 0,
        titleError,
        messagesError,
        visibilityWarning: castedWarnings?.length > 0,
        messagesWarning,
        titleWarning,
      });

      if (castedErrors?.length > 0) {
        return;
      }

      // se non ci sono errori effettuare stampa
      const pdf = responseCasted.data.payload.eventoOutput?.contenuto;
      if (pdf) {
        setPdfModal({
          data: pdf,
          name: 'Anteprima atto',
          open: true,
          isCommunication: false,
        });
      }
    });
  });

  const onVisibilityError = useEventCallback(() => {
    setErrorWarningToVisualize({
      ...errorWarningToVisualize,
      visibilityError: false,
      titleError: '',
      messagesError: [],
    });
  });

  const onVisibilityWarning = useEventCallback(() => {
    setErrorWarningToVisualize({
      ...errorWarningToVisualize,
      visibilityWarning: false,
      titleWarning: '',
      messagesWarning: [],
    });
  });

  return (
    <Fragment>
      {isWithoutBreadcrumbs ? undefined : (
        <Breadcrumbs
          items={
            items ||
            (isCommunications
              ? [
                  ...CommonCivilStatusCrumbs,
                  {
                    name: 'Ricerca Comunicazioni da Atto',
                    path: researchCommunications,
                  },
                  {
                    name: 'Visualizza atto',
                    path: '',
                  },
                ]
              : isNotifications
              ? [
                  ...CommonCivilStatusCrumbs,
                  {
                    name: 'Ricerca Notifiche',
                    path: researchNotificationsPath,
                  },
                  {
                    name: 'Visualizza atto',
                    path: '',
                  },
                ]
              : idCat
              ? [
                  ...CommonCivilStatusCrumbs,
                  {
                    name: 'Ricerca atti',
                    path: researchCatPath,
                  },
                  {
                    name: 'Visualizza atto',
                    path: '',
                  },
                ]
              : [
                  ...CommonCivilStatusCrumbs,
                  {
                    name: 'Ricerca atti',
                    path: researchPath,
                  },
                  {
                    name: 'Visualizza atto',
                    path: '',
                  },
                ])
          }
        />
      )}
      {isLoading ? undefined : (
        <Fragment>
          <Formik initialValues={initialValues as ViewStepVM} onSubmit={noop}>
            <Form>
              <Grid container spacing={2}>
                <TitleStep
                  isView={isView}
                  onGetDataSaved={noop}
                  getTextAct={noop}
                  disabled={isDetail}
                  isCommunications={isCommunications}
                />
                <StepForm
                  goBack={goBack}
                  onSave={noop}
                  onClickRecap={noop}
                  onSaveSubject={noop}
                  onSaveSection={noop}
                  onSaveText={noop}
                  onClickValid={noop}
                  onClickPreview={noop}
                  deleteEvent={noop}
                  onYesPreview={onYesPreview}
                  backCompilation={noop}
                  signatureUSC={noop}
                  onClickVerify={noop}
                  onClickVerifyDocument={noop}
                  getTextAct={noop}
                  setRecap={noop}
                  onClickEditAct={noop}
                  onPrintAct={noop}
                  onConfirmRegistration={noop}
                  onForwardCertificateConformity={noop}
                  transcribeOath={transcribeOath}
                  isView={isView}
                  soggettiUseCaseData={soggettiUseCaseData}
                  disabled={isDetail}
                  isNotifications={isNotifications}
                  isNotificationsAnsc={isNotificationsAnsc}
                  isCommunications={isCommunications}
                  isDetailAcquisitionNotifications={isDetailAcquisitionNotifications}
                  isEditAcquisitionNotifications={isEditAcquisitionNotifications}
                  controlsCommunications={controlsCommunications.data as Array<CommunicationRes>}
                  stateNotification={stateNotification}
                  onClickNotification={onClickNotification}
                  onClickPrintCommunication={onClickPrintCommunication}
                  onParent={onParent}
                  onRegisterBirth={onRegisterBirth}
                  onOther={onOther}
                  onCohabitation={onCohabitation}
                  onRegisterDeath={onRegisterDeath}
                  onRegisterCitizenship={onRegisterCitizenship}
                  onRegisterMarriageCivilUnion={onRegisterMarriageCivilUnion}
                  goBackNotification={goBackNotification}
                  onCancelNotification={onCancelNotification}
                  idNotifica={idNotifica}
                />
                <Grid item xs={12}>
                  <SearchMessages
                    type={MessageType.Error}
                    title={errorWarningToVisualize.titleError || ''}
                    messages={errorWarningToVisualize.messagesError || []}
                    visibility={errorWarningToVisualize.visibilityError || false}
                    onChangeVisibility={onVisibilityError}
                  />
                </Grid>
                <Grid item xs={12}>
                  <SearchMessages
                    type={MessageType.Warning}
                    title={errorWarningToVisualize.titleWarning || ''}
                    messages={errorWarningToVisualize.messagesWarning || []}
                    visibility={errorWarningToVisualize.visibilityWarning || false}
                    onChangeVisibility={onVisibilityWarning}
                  />
                </Grid>
              </Grid>
            </Form>
          </Formik>
          {modalNotificationToDelete ? (
            <DeleteDialog
              isOpen={modalNotificationToDelete}
              onAbort={clearToDeleteNotification}
              onClose={clearToDeleteNotification}
              onConfirm={onTrashPressNotification}
              text="Sei sicuro di voler eliminare la richiesta di aggiornamento? Non sarà più possibile effettuarla in automatico."
            />
          ) : null}
          {pdfModal.open ? (
            <ModalViewerPDF
              open={pdfModal.open}
              data={pdfModal.data}
              name={pdfModal.name}
              title={pdfModal.name || 'Anteprima atto'}
              onClose={() => {
                setModalPrintSuccess((oldState) => ({
                  ...oldState,
                  open: true,
                  isCommunication: pdfModal.isCommunication,
                }));
                setPdfModal({ data: '', open: false, name: '', isCommunication: false });
              }}
            />
          ) : undefined}
          {modalPrintSuccess.open ? (
            <ModalPrintSuccess
              open={modalPrintSuccess.open}
              onYes={() => setModalPrintSuccess({ open: false })}
              onClose={() =>
                modalPrintSuccess.isCommunication
                  ? onClickPrintCommunication(modalPrintSuccess.valuesCommunication)
                  : onYesPreview(idAnscAct || idAnsc)
              }
            />
          ) : undefined}
        </Fragment>
      )}
    </Fragment>
  );
};

StepPageComponent.displayName = 'Step Page';

const StepFromAnsc = memo(StepPageComponent);

export default StepFromAnsc;
